// TODAS as constantes de calibracao do motor vivem aqui, e so aqui.
//
// Regra do projeto: nenhum numero magico espalhado dentro do laco de posse.
// Quando a media da liga sai errada, se conserta neste arquivo e se roda
// `npm run calibrate` -- nunca cacando constantes dentro da simulacao.
//
// A escala de atributo e 0..99, onde 75 = jogador medio da liga. Todo mapeamento
// abaixo e "base da liga + inclinacao * (atributo - 75)", entao um elenco todo
// em 75 tem que reproduzir exatamente as medias-alvo da liga real.

export const AVG = 75; // atributo do jogador medio

// ---------------------------------------------------------------- alvos reais
// Referencia: media NBA moderna. O harness de calibracao compara contra isto.
export const TARGETS = {
  pointsPerGame: 114.0,
  possessions: 99.5,
  fgPct: 0.470,
  threePct: 0.360,
  ftPct: 0.780,
  threeRate: 0.400, // share dos arremessos que sao de 3
  orbPct: 0.260,
  turnovers: 13.2,
  assists: 26.5,
  rebounds: 43.5,
  steals: 7.6,
  blocks: 4.9,
  fouls: 19.5,
  ftAttempts: 22.0,
  pointsStdev: 12.4,   // desvio dos pontos de um time num jogo
  marginStdev: 14.1,   // desvio da margem de vitoria
};

// -------------------------------------------------------------------- relogio
export const CLOCK = {
  quarters: 4,
  quarterSeconds: 12 * 60,
  overtimeSeconds: 5 * 60,
  secondsPerPossession: 14.05, // 2880s / ~200 posses no jogo
  possessionSpread: 4.6,       // desvio da duracao da posse
  // Ritmo e propriedade DA PARTIDA, nao de cada posse: sorteado uma vez, vale
  // para os dois times. Sem isso o total de posses de um jogo quase nao varia
  // (media de 200 sorteios independentes) e os placares dos dois lados ficam
  // descorrelacionados -- o que infla a margem de vitoria muito acima do real.
  gamePaceSpread: 0.55,        // desvio, em segundos por posse, do ritmo do jogo
  minPossessionSeconds: 3,
  maxPossessionSeconds: 24,
  offReboundSeconds: [2, 8],   // tempo extra consumido apos rebote ofensivo
};

// -------------------------------------------------------- resultado da posse
export const RATES = {
  // Turnover por posse.
  turnover: 0.1378,
  turnoverPerHandle: -0.0028,   // handle alto reduz
  turnoverPerDefense: 0.0029,   // defesa adversaria aumenta
  stealShareOfTurnovers: 0.575,
  offensiveFoulShareOfTurnovers: 0.25, // parte dos turnovers e falta de ataque // resto = erro proprio (passe fora, saida de quadra)

  // Falta de arremesso: posse termina na linha do lance livre, sem FGA.
  shootingFoul: 0.0918,
  foulOnThreeShare: 0.20,       // falta em tentativa de 3 rende 3 lances
  andOne: 0.042,                // conversao + falta (gera 1 lance extra)
  nonShootingFoul: 0.0700,      // falta pessoal sem lance livre (bola reposta)
  ftReboundFactor: 0.55,        // rebote apos lance livre errado e mais raro

  // Arremesso
  threeRate: 0.3790,
  threeRatePerAttr: 0.0075,     // tendencia individual de arremessar de 3
  pctTwo: 0.5140,
  pctTwoPerAttr: 0.0042,
  pctThree: 0.3470,
  pctThreePerAttr: 0.0038,
  defenseOnShooting: -0.0036,   // por ponto de defesa acima da media
  freeThrow: 0.7590,
  freeThrowPerAttr: 0.0060,

  // Rebote
  offRebound: 0.2660,
  offReboundPerAttr: 0.0035,
  blockShareOfMissedTwos: 0.184,
  blockShareOfMissedThrees: 0.022,
  reboundLossRate: 0.105,       // erro que nao gera rebote (bola fora)

  // Assistencia (dado o arremesso convertido)
  assistOnTwo: 0.528,
  assistOnThree: 0.868,
  assistPerAttr: 0.0030,
};

// -------------------------------------------------------------------- rotacao
export const ROTATION = {
  maxFouls: 6,            // 6 faltas = fora do jogo (NBA)
  segmentSeconds: 60,     // decide os 5 em quadra a cada minuto de jogo
  minutesPool: 240,       // 5 jogadores x 48 min
  starterCurve: [34, 32, 30, 28, 26],
  benchCurve: [22, 19, 16, 12, 9, 6, 4, 2],
  positionBonus: 8,       // peso para nao escalar 5 pivos
  // Quanto o tecnico evita quem esta a uma falta de sair (em "segundos" de
  // deficit de minutagem, a moeda da rotacao).
  foulRiskPenalty: 420,
};

// ------------------------------------------------- contexto de placar
export const SCORE_EFFECT = {
  // Quem abre vantagem grande administra: ritmo cai, o banco entra, a urgencia
  // troca de lado. Efeito real e observavel -- e o que impede o placar de
  // virar passeio estatistico.
  perPoint: 0.0021,   // ajuste de aproveitamento por ponto de vantagem
  maxLead: 22,        // teto do efeito
  garbageLead: 21,    // vantagem que manda o banco entrar
  garbageSeconds: 300, // ...nos ultimos 5 minutos do jogo
};

// ----------------------------------------------------------- fim de jogo
export const CLUTCH = {
  // Os ultimos segundos de um jogo apertado nao sao jogados como os outros:
  // quem perde por pouco procura os 3 pontos, e quem perde comete falta de
  // proposito para parar o cronometro. Sem isso o motor produz prorrogacao em
  // 2% dos jogos, contra ~6% reais -- e prorrogacao e do que o torcedor lembra.
  seconds: 48,          // janela em que o fim de jogo muda o comportamento
  chaseThreeRate: 0.80, // chance de buscar o 3 quando perde por 3 ou menos
  intentionalFoulSeconds: 34,
  intentionalFoulLead: 3, // faz falta quando perde por ate isso
};

// -------------------------------------------------------------- taticas
/**
 * SISTEMA DE JOGO. Cada escolha tem um CUSTO -- nenhuma e so bonus, senao
 * existe uma opcao certa e as outras viram enfeite.
 */
export const TACTICS = {
  pace: {
    lento: { label: 'Segurar a bola', secs: 1.6, tov: -0.006, hint: 'Menos posses, menos erro. Bom para segurar vantagem.' },
    normal: { label: 'Equilibrado', secs: 0, tov: 0, hint: 'Sem ajuste.' },
    rapido: { label: 'Acelerar', secs: -1.6, tov: 0.010, hint: 'Mais posses e mais pontos -- e mais bola perdida.' },
  },
  offense: {
    garrafao: {
      label: 'Jogar por dentro', threeRate: -0.10, pctTwo: 0.006, foul: 0.007, orb: 0.016,
      hint: 'Mais lances livres e rebote de ataque. Menos pontos de fora.',
    },
    equilibrado: { label: 'Equilibrado', threeRate: 0, pctTwo: 0, foul: 0, orb: 0, hint: 'Sem ajuste.' },
    bombardeio: {
      label: 'Bombardear de 3', threeRate: 0.15, pctThree: 0.002, foul: -0.005, orb: -0.006,
      hint: 'Placar sobe rapido -- e cai rapido. Menos falta sofrida.',
    },
  },
  focus: {
    dividir: { label: 'Dividir a bola', starPower: -0.35, assist: 0.05, hint: 'Todo mundo participa. Mais assistencias.' },
    equilibrado: { label: 'Equilibrado', starPower: 0, assist: 0, hint: 'Sem ajuste.' },
    astro: { label: 'Na mao do astro', starPower: 0.55, assist: -0.06, hint: 'O melhor decide. Se ele apagar, o time apaga.' },
  },
  defense: {
    individual: { label: 'Individual', hint: 'Sem ajuste.', pctTwoAgainst: 0, pctThreeAgainst: 0, tovForced: 0, foulRate: 0, drb: 0 },
    zona: {
      label: 'Zona', pctTwoAgainst: -0.016, pctThreeAgainst: 0.011, tovForced: -0.004, foulRate: -0.010, drb: 0.018,
      hint: 'Fecha o garrafao e o rebote. Entrega o arremesso de fora.',
    },
    pressao: {
      label: 'Pressao', pctTwoAgainst: 0.014, pctThreeAgainst: -0.004, tovForced: 0.022, foulRate: 0.022, drb: -0.012,
      hint: 'Rouba muita bola -- e comete muita falta e sofre em transicao.',
    },
  },
};

export const DEFAULT_TACTICS = {
  pace: 'normal', offense: 'equilibrado', focus: 'equilibrado', defense: 'individual',
};

/** Resolve as taticas de um time num objeto de efeitos ja somados. */
export function resolveTactics(tactics = {}) {
  const t = { ...DEFAULT_TACTICS, ...tactics };
  return {
    keys: t,
    pace: TACTICS.pace[t.pace] || TACTICS.pace.normal,
    offense: TACTICS.offense[t.offense] || TACTICS.offense.equilibrado,
    focus: TACTICS.focus[t.focus] || TACTICS.focus.equilibrado,
    defense: TACTICS.defense[t.defense] || TACTICS.defense.individual,
  };
}

// ------------------------------------------------------------------- vantagem
export const HOME_COURT = {
  // Vantagem de casa real na NBA vale ~2.2 pontos. Aplicada como um leve
  // empurrao no aproveitamento, nao como pontos somados no fim.
  shootingBonus: 0.0100,
  turnoverBonus: -0.0055,
};
